import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AppearanceSettings from '../components/project/AppearanceSettings';
import ProjectMembersPanel from '../components/project/ProjectMembersPanel';
import { Action } from '../lib/auth';
import {
  ArrowLeft, Building2, Calendar, User, Edit, Save, X, Plus,
  CalendarDays, CheckCircle2, Loader2, Clock, ExternalLink
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { toast } from 'sonner';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const {
    activeProject,
    activeProjectRole,
    schedules,
    activeSchedule,
    loadProject,
    updateProject,
    createSchedule,
    setProjectActiveSchedule,
    loading
  } = useProject();

  // MANAGE_MEMBERS is OWNER-only (PLAN level) under RBAC v2 — the owning
  // planner invites/removes members; a pure admin never reaches this page at
  // all (the project 404s for them).
  const canManageMembers = can(Action.MANAGE_MEMBERS, activeProjectRole);
  // Editing project details + schedule management is PLAN-level work — gated by
  // MANAGE_SCHEDULES against the caller's role on the active project (mirrors
  // backend require_can on update_project / schedule routes).
  const canManageSchedules = can(Action.MANAGE_SCHEDULES, activeProjectRole);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [isCreatingSchedule, setIsCreatingSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ name: '', description: '' });

  useEffect(() => {
    if (projectId) {
      loadProject(projectId).catch(() => {
        toast.error('Project not found');
        navigate('/projects');
      });
    }
  }, [projectId, loadProject, navigate]);

  useEffect(() => {
    if (activeProject) {
      setEditData({
        name: activeProject.name || '',
        code: activeProject.code || '',
        code_not_generated: activeProject.code_not_generated || false,
        description: activeProject.description || '',
        planned_start_date: activeProject.planned_start_date || '',
        data_date: activeProject.data_date || '',
        required_finish_date: activeProject.required_finish_date || ''
      });
    }
  }, [activeProject]);

  const handleSave = async () => {
    if (!editData.name?.trim()) {
      toast.error('Project name is required');
      return;
    }

    // Validate code is provided when code_not_generated is false
    if (!editData.code_not_generated && !editData.code?.trim()) {
      toast.error('Project code is required. Check "Code not yet generated" if the code is pending.');
      return;
    }

    setIsSaving(true);
    try {
      await updateProject(projectId, {
        name: editData.name.trim(),
        code: editData.code_not_generated ? null : (editData.code?.trim() || null),
        code_not_generated: editData.code_not_generated,
        description: editData.description?.trim() || null,
        planned_start_date: editData.planned_start_date || null,
        data_date: editData.data_date || null,
        required_finish_date: editData.required_finish_date || null
      });
      toast.success('Project updated successfully');
      setIsEditing(false);
    } catch (error) {
      const message = apiErrorMessage(error, 'Failed to update project');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorSchemeChange = async (newScheme) => {
    try {
      await updateProject(projectId, { color_scheme: newScheme });
      toast.success('Color scheme updated');
    } catch (error) {
      const message = apiErrorMessage(error, 'Failed to update color scheme');
      toast.error(message);
    }
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (!newSchedule.name.trim()) {
      toast.error('Schedule name is required');
      return;
    }

    setIsCreatingSchedule(true);
    try {
      await createSchedule({
        name: newSchedule.name.trim(),
        description: newSchedule.description?.trim() || null,
        project_id: projectId
      });
      toast.success('Schedule created successfully');
      setShowCreateSchedule(false);
      setNewSchedule({ name: '', description: '' });
    } catch (error) {
      const message = apiErrorMessage(error, 'Failed to create schedule');
      toast.error(message);
    } finally {
      setIsCreatingSchedule(false);
    }
  };

  const handleSetActiveSchedule = async (scheduleId) => {
    try {
      await setProjectActiveSchedule(projectId, scheduleId);
      toast.success('Active schedule updated');
    } catch (error) {
      const message = apiErrorMessage(error, 'Failed to set active schedule');
      toast.error(message);
    }
  };

  // Handle clicking a schedule to open it in the Scheduling page
  const handleOpenSchedule = async (schedule) => {
    try {
      // Set the schedule as active if it's not already
      if (!schedule.is_active) {
        await setProjectActiveSchedule(projectId, schedule.id);
      }
      // Navigate to the Scheduling page
      navigate('/scheduling');
    } catch (error) {
      const message = apiErrorMessage(error, 'Failed to open schedule');
      toast.error(message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !activeProject) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!activeProject) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          onClick={() => navigate('/projects')}
          data-testid="back-to-projects"
        >
          <ArrowLeft className="h-4 w-4" />
          Projects
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <Building2 className="h-6 w-6 text-slate-500" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 truncate">
              {activeProject.name}
            </h1>
            {activeProject.description && (
              <p className="text-sm text-slate-500 mt-0.5">{activeProject.description}</p>
            )}
          </div>
        </div>
        
        {canManageSchedules && !isEditing && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setIsEditing(true)}
            data-testid="edit-project-button"
          >
            <Edit className="h-4 w-4" />
            Edit Project
          </Button>
        )}
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance">Appearance</TabsTrigger>
          {canManageMembers && (
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details" className="mt-0">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project Details - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Information Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Project Information
                </CardTitle>
                {isEditing && (
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setIsEditing(false)}
                      disabled={isSaving}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-slate-900 hover:bg-slate-800 gap-2"
                      onClick={handleSave}
                      disabled={isSaving}
                      data-testid="save-project-button"
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-900">Project Name</Label>
                    <Input
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="h-9"
                      data-testid="edit-project-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-900">
                      Project Code {!editData.code_not_generated && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      value={editData.code}
                      onChange={(e) => setEditData({ ...editData, code: e.target.value.toUpperCase() })}
                      placeholder={editData.code_not_generated ? 'N/A' : 'e.g., PRJ-001'}
                      className={`h-9 font-mono text-sm ${editData.code_not_generated ? 'bg-slate-50 text-slate-400' : ''}`}
                      maxLength={20}
                      disabled={editData.code_not_generated}
                      data-testid="edit-project-code"
                    />
                    <div className="flex items-center gap-2 mt-1">
                      <Checkbox
                        id="edit_code_not_generated"
                        checked={editData.code_not_generated}
                        onCheckedChange={(checked) => setEditData({
                          ...editData,
                          code_not_generated: !!checked,
                          code: checked ? '' : editData.code
                        })}
                        data-testid="edit-code-not-generated-checkbox"
                      />
                      <Label
                        htmlFor="edit_code_not_generated"
                        className="text-xs text-slate-500 cursor-pointer select-none"
                      >
                        Code not yet generated
                      </Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-900">Description</Label>
                    <Input
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="h-9"
                      data-testid="edit-project-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-900">Planned Start Date</Label>
                      <Input
                        type="date"
                        value={editData.planned_start_date}
                        onChange={(e) => setEditData({ ...editData, planned_start_date: e.target.value })}
                        className="h-9"
                        data-testid="edit-start-date"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-900">Required Finish Date</Label>
                    <Input
                      type="date"
                      value={editData.required_finish_date}
                      onChange={(e) => setEditData({ ...editData, required_finish_date: e.target.value })}
                      className="h-9"
                      data-testid="edit-finish-date"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Project Code</p>
                    <span className={`text-sm font-mono font-medium ${
                      activeProject.code_not_generated
                        ? 'text-amber-600 italic'
                        : 'text-slate-900'
                    }`}>
                      {activeProject.code || (activeProject.code_not_generated ? 'Pending - not yet generated' : '—')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500 mb-1">Planned Start Date</p>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {formatDate(activeProject.planned_start_date)}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500 mb-1">Required Finish Date</p>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {formatDate(activeProject.required_finish_date)}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500 mb-1">Created By</p>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {activeProject.created_by_name || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Schedules Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Schedules
                </CardTitle>
                {canManageSchedules && (
                  <Button
                    size="sm"
                    className="h-8 gap-2 bg-slate-900 hover:bg-slate-800"
                    onClick={() => setShowCreateSchedule(true)}
                    data-testid="create-schedule-button"
                  >
                    <Plus className="h-4 w-4" />
                    New Schedule
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {schedules.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <CalendarDays className="h-6 w-6 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 mb-1">No schedules yet</p>
                  <p className="text-xs text-slate-400">
                    Create a schedule to start planning
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules.map((schedule) => (
                    <div 
                      key={schedule.id}
                      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                        schedule.is_active 
                          ? 'border-slate-900 bg-slate-50 hover:bg-slate-100' 
                          : 'border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                      }`}
                      onClick={() => handleOpenSchedule(schedule)}
                      data-testid={`schedule-item-${schedule.id}`}
                    >
                      <div className="h-10 w-10 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <CalendarDays className="h-5 w-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors truncate min-w-0">
                            {schedule.name}
                          </p>
                          {schedule.is_active && (
                            <Badge className="bg-slate-900 text-white text-[10px] shrink-0">Active</Badge>
                          )}
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {schedule.status}
                          </Badge>
                        </div>
                        {schedule.description && (
                          <p className="text-xs text-slate-400 mt-0.5">{schedule.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {schedule.created_by_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(schedule.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {canManageSchedules && !schedule.is_active && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetActiveSchedule(schedule.id);
                            }}
                            data-testid={`set-active-${schedule.id}`}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Set Active
                          </Button>
                        )}
                        <div className="text-slate-400 group-hover:text-slate-600">
                          <ExternalLink className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Active Schedule Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-900">
                Active Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeSchedule ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center">
                      <CalendarDays className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{activeSchedule.name}</p>
                      <p className="text-xs text-slate-400">{activeSchedule.status}</p>
                    </div>
                  </div>
                  <Separator />
                  <div className="text-xs text-slate-500">
                    <p>This schedule is currently active for this project.</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500">No active schedule</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Create a schedule to get started
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Project Metadata */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-900">
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Project ID</p>
                <p className="text-xs font-mono text-slate-600 truncate">{activeProject.id}</p>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-slate-500">Created</p>
                <p className="text-sm text-slate-900">{formatDateTime(activeProject.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Last Updated</p>
                <p className="text-sm text-slate-900">{formatDateTime(activeProject.updated_at)}</p>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-slate-500">Total Schedules</p>
                <p className="text-sm font-medium text-slate-900">{schedules.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="appearance" className="mt-0" data-testid="appearance-tab-content">
          <div className="max-w-2xl">
            <AppearanceSettings
              value={activeProject?.color_scheme}
              onChange={handleColorSchemeChange}
              disabled={!canManageSchedules}
            />
          </div>
        </TabsContent>

        {canManageMembers && (
          <TabsContent value="members" className="mt-0" data-testid="members-tab-content">
            <ProjectMembersPanel projectId={projectId} ownerId={activeProject?.owner_id} />
          </TabsContent>
        )}
      </Tabs>

      {/* Create Schedule Dialog */}
      <Dialog open={showCreateSchedule} onOpenChange={setShowCreateSchedule}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">Create New Schedule</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Add a new schedule to this project.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSchedule}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="schedule-name" className="text-sm font-medium text-slate-900">
                  Schedule Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="schedule-name"
                  placeholder="e.g., Baseline Schedule v1"
                  value={newSchedule.name}
                  onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
                  className="h-9"
                  data-testid="schedule-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="schedule-description" className="text-sm font-medium text-slate-900">
                  Description
                </Label>
                <Input
                  id="schedule-description"
                  placeholder="Brief description"
                  value={newSchedule.description}
                  onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
                  className="h-9"
                  data-testid="schedule-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateSchedule(false)}
                disabled={isCreatingSchedule}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={isCreatingSchedule}
                data-testid="create-schedule-submit"
              >
                {isCreatingSchedule ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Schedule'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetailPage;
